import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useModal } from '../../hooks/modalContext';
import { ArrowRight, ChevronLeft, X } from 'lucide-react';
import { getGiftCards, getGiftCardsDetails } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { motion } from 'framer-motion';
import { Icon } from '../../Icons/Icon';

import { usePoints } from '../../hooks/pointsContext';
import HtmlOrText from '../../utils/HtmlOrText';
import { stripHtml } from '../../utils/sanitize';

export interface GiftCard {
  brand_code: string;
  name: string;
  image_url: string;
  disclaimer: string;
}

export interface GiftCardDetail {
  brand: {
    brand_code: string;
    name: string;
    image_url: string;
    disclaimer: string;
    description: string;
    variable_price: boolean;
    min_price_in_cents: number;
    max_price_in_cents: number;
    allowed_prices_in_cents: number[];
  };
}

const limit = 10;

const AllGiftCards: React.FC = () => {
  const { setModal, modalState } = useModal();
  const fromModal = modalState?.fromModal || '';
  const { auth } = useAuth();
  const { points } = usePoints();
  const [searchTerm, setSearchTerm] = useState('');
  const [giftCards, setGiftCards] = useState<GiftCard[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState<GiftCardDetail | null>(null);
  const observerRef = useRef<HTMLDivElement | null>(null);
  const brandCode = modalState?.brandCode || '';
  const [activeBrandCode, setActiveBrandCode] = useState<string | null>(null);

  const fetchBrands = useCallback(
    async (term: string, pageNum: number) => {
      setLoading(true);
      try {
        const response = await getGiftCards(auth?.data.token, pageNum, limit, term);
        const newCards = response?.data || [];
        const meta = response?.meta;

        setGiftCards((prev) => (pageNum === 1 ? newCards : [...prev, ...newCards]));
        setHasMore(pageNum < (meta?.lastPage ?? 0));
      } catch (error) {
        console.error('Error fetching brands for user.', error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [auth]
  );
  useEffect(() => {
    if (brandCode) {
      fetchGiftCardDetails(brandCode);
    } else {
      setPage(1);
      fetchBrands(searchTerm, 1);
    }
  }, [brandCode, fetchBrands, searchTerm]);

  const fetchGiftCardDetails = async (brand_code: string) => {
    try {
      setDetailLoading(true);
      if (brand_code) {
        const response = await getGiftCardsDetails(auth?.data.token, brand_code);
        setSelectedCard(response);
      }
    } catch (error) {
      console.error('Error fetching gift card details:', error);
    } finally {
      setDetailLoading(false);
      setActiveBrandCode(null);
    }
  };

  const handleCardClick = async (card: GiftCard) => {
    setActiveBrandCode(card.brand_code);
    await fetchGiftCardDetails(card.brand_code);
  };

  useEffect(() => {
    setPage(1);
    fetchBrands(searchTerm, 1);
  }, [searchTerm, fetchBrands]);

  useEffect(() => {
    if (page === 1) return;
    fetchBrands(searchTerm, page);
  }, [page, fetchBrands, searchTerm]);

  useEffect(() => {
    if (loading || !hasMore) return;
    const container = document.querySelector('.scroll-container');
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      {
        root: container,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    const current = observerRef.current;
    if (current) observer.observe(current);

    return () => {
      if (current) observer.unobserve(current);
    };
  }, [loading, hasMore]);

  const renderGiftCardDetails = () => {
    if (!selectedCard) return null;
    const { brand } = selectedCard;
    const redeemGiftCard = () => {
      setModal('redeem-gift-card', {
        giftPrices: brand.allowed_prices_in_cents,
        brandCode: brand.brand_code,
        fromModal: 'all-gift-cards',
      });
    };

    return (
      <motion.div
        key={brand.brand_code}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className='flex flex-col gap-8 py-4'
      >
        <div className='flex flex-col items-center text-center px-2'>
          <div
            className='w-[150px] h-[150px] bg-center bg-cover bg-no-repeat rounded-xl mb-4'
            style={{ backgroundImage: `url(${brand.image_url})` }}
          />
          {brand.description && (
            <div className='w-full items-start flex flex-col gap-2'>
              <span className='text-main text-base font-bold'>Description</span>
              <HtmlOrText className='text-base text-main text-start' content={brand.description} />
            </div>
          )}

          {brand.disclaimer && (
            <HtmlOrText
              className='text-xs text-red-500 mt-2 text-start'
              content={brand.disclaimer}
            />
          )}

          <button className='submitBtn mt-5' onClick={redeemGiftCard}>
            <div className='flex justify-between p-6 w-full'>
              <span>Redeem TP points</span>
              <ArrowRight />
            </div>
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      className='scroll-container relative w-[992px] max-h-[852px] bg-[#FFF] rounded-[12px] flex flex-col overflow-y-auto max-h-[90vh] max-w-[90vw] px-5'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Header */}
      <div className='flex justify-end py-4 '>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={() => setModal(null)}
        />
      </div>
      <div className='flex justify-between items-center w-full mb-5 px-4'>
        <div className='flex justify-center items-center gap-2'>
          <ChevronLeft
            className='h-5 w-5 cursor-pointer'
            onClick={() =>
              selectedCard
                ? setSelectedCard(null)
                : fromModal
                  ? setModal('rewards', { openTab: 'redeem' })
                  : setModal(null)
            }
          />
          <span className='text-main text-[20px]'>
            {selectedCard ? selectedCard?.brand.name : 'Gift cards'}
          </span>
        </div>

        <div
          className='flex items-center gap-2 bg-[var(--Primary-Color,#0055FF)] rounded-full px-3 py-2 h-9 cursor-pointer'
          onClick={() => setModal('rewards')}
        >
          <Icon name='wallet' color='rgba(236,188,33,1)' className='w-6 h-6' />
          <span className='text-white text-lg'>
            {Number(points?.data?.points?.totalAvailable ?? 0).toLocaleString('en-US')}
          </span>
        </div>
      </div>

      {selectedCard ? (
        renderGiftCardDetails()
      ) : (
        <>
          {/* Search */}
          <div className='inputWrapper mb-5 px-4'>
            <div className='inputField'>
              <input
                type='text'
                placeholder='Search gift cards...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
              />
            </div>
          </div>

          {/* Gift Cards Grid */}
          <div
            className='
            px-4
   grid grid-cols-1   
  xxs:grid-cols-2     
  lgx:grid-cols-5    
  gap-6 items-stretch
'
          >
            {giftCards.map((card) => {
              const tooltip = stripHtml(card.disclaimer || '');
              const isLoadingCard = detailLoading && activeBrandCode === card.brand_code;

              return (
                <div
                  key={card.brand_code}
                  className='relative w-full flex items-center cursor-pointer hover:scale-105 transition-transform duration-200 ease-in-out'
                  onClick={() => handleCardClick(card)}
                >
                  {/* Glow Shadow */}
                  <div className='absolute bottom-1 left-1/2 translate-x-[-50%] w-[50%] h-[50px] rounded-[20px] shadow-[0px_12px_50px_0px_#A6B1CB] z-0 pointer-events-none'></div>

                  {/* Card Content */}
                  <div
                    title={tooltip || undefined}
                    className='relative z-10 flex flex-col items-center gap-4 p-5 border border-[#E9EDF9] rounded-[20px] bg-white h-full w-full'
                  >
                    <div className='flex items-center justify-center w-full relative'>
                      <div
                        className='w-[110px] h-[110px] flex-shrink-0 aspect-square bg-center bg-cover bg-no-repeat bg-[lightgray]'
                        style={{ backgroundImage: `url(${card.image_url})` }}
                        aria-label={card.name}
                        role='img'
                      />

                      {isLoadingCard && (
                        <div className='absolute inset-0 flex items-center justify-center bg-white/70 rounded-xl'>
                          <div className='w-6 h-6 border-2 border-t-transparent border-main rounded-full animate-spin'></div>
                        </div>
                      )}
                    </div>
                    <div className='w-full items-start'>
                      <span className='text-sm text-main font-medium break-words'>{card.name}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty or loading */}
          {loading && giftCards.length === 0 && (
            <div
              className='px-4 grid grid-cols-1   
  xxs:grid-cols-2     
  lgx:grid-cols-6    
  gap-6 items-stretch gap-6'
            >
              {Array.from({ length: 10 }).map((_, idx) => (
                <GiftCardSkeleton key={idx} />
              ))}
            </div>
          )}
          {!loading && giftCards.length === 0 && (
            <p className='text-center text-gray-400 mt-10'>No gift cards found.</p>
          )}

          {/* Observer trigger */}
          <div ref={observerRef} className='h-[1px] mt-10' />
        </>
      )}
    </motion.div>
  );
};

export default AllGiftCards;

const GiftCardSkeleton = () => {
  return (
    <div className='animate-pulse w-full flex items-center'>
      <div className='relative w-full flex flex-col items-center gap-4 p-5 border border-[#E9EDF9] rounded-[20px] bg-white h-full'>
        <div className='w-[110px] h-[110px] bg-gray-200 rounded-xl'></div>
        <div className='w-3/4 h-4 bg-gray-200 rounded'></div>
      </div>
    </div>
  );
};
